import React, { useEffect, useRef, useState } from 'react';
import { 
  Activity, 
  Globe, 
  Code2, 
  Video, 
  FileCode2, 
  PenTool, 
  Image, 
  Gauge, 
  Layers, 
  Sparkles, 
  Layout 
} from 'lucide-react';
import { SKILLS } from '../constants';
import { useLanguage } from '../context/LanguageContext';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  SiWordpress: Globe,
  SiHtml5: FileCode2,
  SiReact: Code2,
  SiCanva: Layout,
  SiFigma: Layers,
  SiSpeedtest: Gauge,
  Code2: Code2,
  Video: Video,
  PenTool: PenTool,
  Image: Image,
  Sparkles: Sparkles,
};

const Skills: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t, isUrdu } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="skills" 
      aria-label="Core Technical Skills & Stack - WordPress, React, AI Video, UI/UX"
      ref={sectionRef} 
      className="py-14 sm:py-20 bg-slate-50 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 mb-16 relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white border border-slate-200 rounded-full mb-6 shadow-sm">
            <Activity size={14} className="text-indigo-600" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
              {t.skills.badge}
            </span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-900 mb-6">
            {t.skills.headingPrefix} <span className="text-indigo-600">{t.skills.headingHighlight}</span>
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl leading-relaxed font-normal">
            {t.skills.subheading}
          </p>
        </div>
      </div>
      
      {/* Infinite Horizontal Marquee */}
      <div className="relative flex overflow-hidden group">
        <div className="flex animate-marquee gap-8 py-10 will-change-transform">
          {[...SKILLS, ...SKILLS].map((skill, index) => {
            const Icon = iconMap[skill.icon];
            return (
              <div 
                key={index} 
                className="min-w-[280px] sm:min-w-[380px] p-8 sm:p-10 bg-white rounded-[2rem] border border-slate-200 transition-all duration-300 hover:border-indigo-300 hover:shadow-xl relative overflow-hidden will-change-transform shadow-sm"
              >
                <div className="flex justify-between items-start mb-8 relative z-10">
                  <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100 shadow-sm">
                    {Icon && <Icon size={24} />}
                  </div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
                    {skill.percentage >= 90 ? (isUrdu ? 'ماہر' : 'Master') : (isUrdu ? 'پیشہ ور' : 'Expert')}
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 mb-6 tracking-tight relative z-10 leading-tight min-h-[3.5rem] flex items-center">
                  {skill.name}
                </h3>

                <div className="space-y-4 relative z-10">
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-500">
                    <span>{t.skills.proficiency}</span>
                    <span className="text-slate-900">{skill.percentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                    <div 
                      className="h-full bg-indigo-600 transition-all duration-1000 ease-out rounded-full"
                      style={{ width: isVisible ? `${skill.percentage}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;